import { useEffect } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { imageProxySrc } from '../../lib/asset.js'

/**
 * In-app image viewer. Renders the full image over a dark backdrop instead of
 * opening a new browser tab. Esc / backdrop / ✕ closes; ← → (or the arrows)
 * navigate when there is more than one image.
 *
 * @param images  array of file objects (location/thumbnail/url/name)
 * @param index   currently-shown index, or null/undefined = closed
 * @param onClose ()  · onIndex (i) to move to another image
 */
export default function ImageLightbox({ images, index, onClose, onIndex }) {
  const list = Array.isArray(images) ? images.filter((f) => f && (f.location || f.thumbnail || f.url)) : []
  const open = index != null && list[index]

  useEffect(() => {
    if (!open) return
    function onKey(e) {
      if (e.key === 'Escape') onClose?.()
      else if (e.key === 'ArrowLeft') onIndex?.((index - 1 + list.length) % list.length)
      else if (e.key === 'ArrowRight') onIndex?.((index + 1) % list.length)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, index, list.length, onClose, onIndex])

  if (!open) return null
  const img = list[index]
  const many = list.length > 1
  const go = (i) => onIndex?.((i + list.length) % list.length)

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm" onMouseDown={onClose} role="dialog" aria-modal="true">
      <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10" aria-label="ปิด"><X size={22} /></button>

      {many && (
        <button onMouseDown={(e) => { e.stopPropagation(); go(index - 1) }} className="absolute left-3 sm:left-6 p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10" aria-label="ก่อนหน้า"><ChevronLeft size={30} /></button>
      )}

      <img
        src={imageProxySrc(img)}
        alt={img.name || ''}
        className="max-h-[88vh] max-w-[92vw] object-contain rounded-lg shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      />

      {many && (
        <button onMouseDown={(e) => { e.stopPropagation(); go(index + 1) }} className="absolute right-3 sm:right-6 p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10" aria-label="ถัดไป"><ChevronRight size={30} /></button>
      )}

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[12px] text-white/75 bg-black/40 px-3 py-1 rounded-full">
        {img.name ? img.name : ''}{many ? `${img.name ? ' · ' : ''}${index + 1}/${list.length}` : ''}
      </div>
    </div>
  )
}
